import api from '../api/api';
import { generateZplLabel, generateZplTicket, LABEL_PRESETS, downloadZplFile } from '../utils/zplGenerator';
import { formatProductQrValue, formatProductCodeValue } from '../utils/codeResolver';

// Constantes para comandos ESC/POS básicos (para impresoras térmicas serie/bluetooth)
const ESC = '\x1B';
const GS = '\x1D';
const COMMANDS = {
  INIT: `${ESC}@`,
  ALIGN_CENTER: `${ESC}a\x01`,
  ALIGN_LEFT: `${ESC}a\x00`,
  ALIGN_RIGHT: `${ESC}a\x02`,
  BOLD_ON: `${ESC}E\x01`,
  BOLD_OFF: `${ESC}E\x00`,
  CUT: `${GS}V\x41\x00`,
  LINE_FEED: '\n',
};

class PrinterService {
  constructor() {
    this.connection = null;
    this.config = this.getLocalConfig();
  }

  getLocalConfig() {
    try {
      const saved = localStorage.getItem('posent_printer_config');
      if (saved) return JSON.parse(saved);
    } catch (e) {
      console.warn('Error reading local printer config:', e);
    }
    return {
      model: 'RONGTA RP425 (ZPL 203DPI)',
      printerType: 'thermal_ticket', // 'thermal_label' | 'thermal_ticket' | 'standard'
      connection: 'usb',
      paperWidth: '80',
      defaultLabelSize: '50x25',
      emulation: 'ZPL',
      darkness: 15,
      autocut: false,
    };
  }

  saveLocalConfig(config) {
    try {
      this.config = { ...this.config, ...config };
      localStorage.setItem('posent_printer_config', JSON.stringify(this.config));
    } catch (e) {
      console.warn('Error saving local printer config:', e);
    }
  }

  /**
   * Obtiene la configuración de impresora desde MongoDB o fallback local
   */
  async getPrinterConfig() {
    try {
      const response = await api.get('/settings/printer');
      if (response.data && response.data.data) {
        const remoteConfig = response.data.data;
        this.saveLocalConfig(remoteConfig);
        return remoteConfig;
      }
    } catch (error) {
      console.warn('No se pudo cargar config de impresora de la nube, usando local:', error.message);
    }
    return this.config;
  }

  /**
   * Guarda la configuración de impresora en MongoDB y local
   */
  async savePrinterConfig(userId, config) {
    this.saveLocalConfig(config);
    try {
      await api.patch('/settings/printer', config);
      return true;
    } catch (error) {
      console.warn('Guardado en nube falló, conservando local:', error.message);
      return true;
    }
  }

  /**
   * Imprime etiquetas de productos con alta precisión milimétrica (optimizado para Rongta RP425 y estándar)
   * @param {Object|Array} products - Producto único o lista de productos a imprimir
   * @param {Object} options - Opciones de formato (size, copies, type: 'barcode'|'qr', showPrice, showName, etc.)
   */
  async printLabel(products, options = {}) {
    const items = Array.isArray(products) ? products : [products];
    if (items.length === 0 || !items[0]) {
      throw new Error('No hay productos seleccionados para imprimir etiqueta');
    }

    const {
      size = this.config?.defaultLabelSize || '50x25',
      copies = 1,
      type = 'barcode', // 'barcode' | 'qr'
      orientation = 'horizontal', // 'horizontal' | 'vertical'
      showPrice = true,
      showName = true,
      showCode = true,
      showCategory = false,
      showBusinessName = false,
      businessName = 'POSENT',
      useStockCount = false,
    } = options;

    const preset = LABEL_PRESETS[size] || LABEL_PRESETS['50x25'];
    const widthMm = preset.widthMm;
    const heightMm = preset.heightMm;
    const finalWidthMm = orientation === 'vertical' ? heightMm : widthMm;
    const finalHeightMm = orientation === 'vertical' ? widthMm : heightMm;

    // Crear iframe oculto para impresión exacta
    return new Promise(async (resolve, reject) => {
      try {
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = `${finalWidthMm}mm`;
        iframe.style.height = `${finalHeightMm}mm`;
        iframe.style.border = '0';
        iframe.style.zIndex = '-9999';
        document.body.appendChild(iframe);

        const iframeDoc = iframe.contentWindow.document;

        // Generar imágenes QR o Códigos de barras localmente usando qrcode y jsbarcode
        let QRCodeLib = null;
        let JsBarcodeLib = null;
        try {
          QRCodeLib = (await import('qrcode')).default;
        } catch (e) {
          console.warn('qrcode import error:', e);
        }
        try {
          JsBarcodeLib = (await import('jsbarcode')).default;
        } catch (e) {
          console.warn('jsbarcode import error:', e);
        }

        // Calcular alturas balanceadas en base a la altura total de la etiqueta para que NUNCA desborde
        // Por ejemplo, para 50x25mm:
        const isCompact = heightMm <= 25;
        const qrSizeMm = isCompact 
          ? (showName && showPrice ? 12 : 15)
          : (showName && showPrice ? 16 : 22);
        const barcodeHeightMm = isCompact ? 10 : 14;
        const titleFontSize = isCompact ? '7.5pt' : '8.5pt';
        const priceFontSize = isCompact ? '9.5pt' : '12pt';
        const bizFontSize = isCompact ? '6.5pt' : '7.5pt';

        // Helper para generar DataURL local de QR
        const generateQrDataUrl = async (text) => {
          if (QRCodeLib && QRCodeLib.toDataURL) {
            try {
              return await QRCodeLib.toDataURL(text, {
                errorCorrectionLevel: 'M',
                margin: 0,
                width: 300,
                color: { dark: '#000000', light: '#ffffff' }
              });
            } catch (err) {
              console.warn('QRCodeLib toDataURL error:', err);
            }
          }
          // Fallback a API con margin=0
          return `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(text)}&ecc=M&margin=0`;
        };

        // Helper para generar DataURL local de Código de Barras (con zona muda/quiet zone para lector láser)
        const generateBarcodeDataUrl = (code) => {
          if (JsBarcodeLib) {
            try {
              const canvas = document.createElement('canvas');
              JsBarcodeLib(canvas, code, {
                format: 'CODE128',
                displayValue: true, // Texto legible debajo de las barras
                fontSize: 12,
                font: 'Arial, sans-serif',
                textMargin: 2,
                margin: 8, // Margen blanco crítico para lectura por escáner de código de barras
                height: isCompact ? 50 : 65,
                width: 2,
                background: '#ffffff',
                lineColor: '#000000'
              });
              return canvas.toDataURL('image/png');
            } catch (err) {
              console.warn('JsBarcodeLib toDataURL error:', err);
            }
          }
          return `https://bwipjs-api.metafloor.com/?bcid=code128&text=${encodeURIComponent(code)}&scale=3&height=14&includetext=true&barcolor=000000&guardwhitespace=true`;
        };

        // Cache de imágenes generadas para no recalcular por cada copia
        const itemMediaCache = new Map();
        for (const item of items) {
          const codeVal = formatProductCodeValue(item);
          const qrData = formatProductQrValue(item);
          const media = {
            qrUrl: await generateQrDataUrl(qrData),
            barcodeUrl: generateBarcodeDataUrl(codeVal)
          };
          itemMediaCache.set(codeVal, media);
        }

        // Generar HTML de cada etiqueta
        let labelsHtml = '';
        items.forEach((item) => {
          const qty = useStockCount ? (parseInt(item.stock || item.stock_quantity) || 1) : copies;
          const codeVal = formatProductCodeValue(item);
          const nameVal = item.name || item.nombre || 'Producto';
          const priceVal = typeof item.price === 'number' ? `$${item.price.toFixed(2)}` : (item.price ? `$${item.price}` : '$0.00');
          const catVal = item.category || '';
          const media = itemMediaCache.get(codeVal) || {};

          for (let i = 0; i < qty; i++) {
            labelsHtml += `
              <div class="label-box">
                ${showBusinessName ? `<div class="label-biz">${businessName}</div>` : ''}
                ${showName ? `<div class="label-title" title="${nameVal}">${nameVal}</div>` : ''}
                ${showCode ? `
                  <div class="label-code-wrapper">
                    ${type === 'qr' 
                      ? `<img src="${media.qrUrl}" class="label-qr" alt="QR" />`
                      : `<img src="${media.barcodeUrl}" class="label-barcode" alt="${codeVal}" />`
                    }
                  </div>
                  <div class="label-code-text">CÓD: ${codeVal}</div>
                ` : ''}
                ${showCategory && catVal ? `<div class="label-cat">${catVal}</div>` : ''}
                ${showPrice ? `<div class="label-price">${priceVal}</div>` : ''}
              </div>
            `;
          }
        });

        iframeDoc.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <meta charset="utf-8" />
              <title>Etiquetas RP425 (${finalWidthMm}x${finalHeightMm}mm)</title>
              <style>
                @page {
                  size: ${finalWidthMm}mm ${finalHeightMm}mm;
                  margin: 0mm !important;
                }
                @media print {
                  @page {
                    size: ${finalWidthMm}mm ${finalHeightMm}mm;
                    margin: 0mm !important;
                  }
                  html, body {
                    width: ${finalWidthMm}mm !important;
                    height: ${finalHeightMm}mm !important;
                    max-width: ${finalWidthMm}mm !important;
                    max-height: ${finalHeightMm}mm !important;
                    margin: 0 !important;
                    padding: 0 !important;
                    overflow: hidden !important;
                  }
                }
                * {
                  box-sizing: border-box;
                  margin: 0;
                  padding: 0;
                }
                html, body {
                  width: ${finalWidthMm}mm;
                  height: ${finalHeightMm}mm;
                  margin: 0;
                  padding: 0;
                  background: #ffffff !important;
                  font-family: Arial, "Helvetica Neue", sans-serif;
                  color: #000000 !important;
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
                .label-box {
                  width: ${finalWidthMm}mm;
                  height: ${finalHeightMm}mm;
                  max-width: ${finalWidthMm}mm;
                  max-height: ${finalHeightMm}mm;
                  box-sizing: border-box;
                  padding: 1mm 1.5mm;
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: space-evenly;
                  text-align: center;
                  page-break-inside: avoid;
                  page-break-after: always;
                  break-after: page;
                  overflow: hidden;
                }
                .label-biz {
                  font-size: ${bizFontSize};
                  font-weight: 800;
                  color: #000000 !important;
                  text-transform: uppercase;
                  line-height: 1;
                  max-width: 96%;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                }
                .label-title {
                  font-size: ${titleFontSize};
                  font-weight: 800;
                  color: #000000 !important;
                  line-height: 1.1;
                  max-width: 98%;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  letter-spacing: -0.01em;
                }
                .label-code-wrapper {
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  width: 100%;
                  overflow: hidden;
                  flex-shrink: 0;
                }
                .label-barcode {
                  max-width: 96%;
                  height: ${barcodeHeightMm}mm;
                  max-height: ${barcodeHeightMm}mm;
                  object-fit: contain;
                  background: #ffffff;
                  image-rendering: pixelated;
                  image-rendering: crisp-edges;
                }
                .label-qr {
                  width: ${qrSizeMm}mm;
                  height: ${qrSizeMm}mm;
                  max-width: ${qrSizeMm}mm;
                  max-height: ${qrSizeMm}mm;
                  object-fit: contain;
                  image-rendering: pixelated;
                  image-rendering: crisp-edges;
                }
                .label-code-text {
                  font-size: 7.5pt;
                  font-weight: 900 !important;
                  color: #000000 !important;
                  font-family: Consolas, "Courier New", Courier, monospace, Arial, sans-serif !important;
                  line-height: 1;
                  letter-spacing: 0.04em;
                  margin: 0.5mm 0;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  max-width: 98%;
                }
                .label-cat {
                  font-size: 6pt;
                  font-weight: 700;
                  color: #000000 !important;
                  text-transform: uppercase;
                  line-height: 1;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  max-width: 95%;
                }
                .label-price {
                  font-size: ${priceFontSize};
                  font-weight: 900 !important;
                  color: #000000 !important;
                  line-height: 1;
                  letter-spacing: -0.02em;
                  font-family: "Arial Black", Impact, Arial, sans-serif !important;
                }
              </style>
            </head>
            <body>
              ${labelsHtml}
              <script>
                // Esperar a que todas las imágenes estén completamente cargadas antes de imprimir
                function triggerPrintWhenReady() {
                  var images = document.getElementsByTagName('img');
                  var loadedCount = 0;
                  var totalImages = images.length;

                  if (totalImages === 0) {
                    executePrint();
                    return;
                  }

                  function checkAllLoaded() {
                    loadedCount++;
                    if (loadedCount >= totalImages) {
                      executePrint();
                    }
                  }

                  for (var i = 0; i < totalImages; i++) {
                    if (images[i].complete) {
                      checkAllLoaded();
                    } else {
                      images[i].addEventListener('load', checkAllLoaded);
                      images[i].addEventListener('error', checkAllLoaded);
                    }
                  }
                }

                function executePrint() {
                  setTimeout(function() {
                    window.focus();
                    window.print();
                    setTimeout(function() {
                      if (window.parent && window.frameElement) {
                        try {
                          window.parent.document.body.removeChild(window.frameElement);
                        } catch(e) {}
                      }
                    }, 800);
                  }, 250);
                }

                if (document.readyState === 'complete') {
                  triggerPrintWhenReady();
                } else {
                  window.addEventListener('load', triggerPrintWhenReady);
                }
              </script>
            </body>
          </html>
        `);
        iframeDoc.close();
        resolve(true);
      } catch (err) {
        console.error('Error al generar impresión de etiquetas:', err);
        reject(err);
      }
    });
  }

  /**
   * Genera el código ZPL para un producto o conjunto de productos
   */
  getZplForProduct(product, options) {
    return generateZplLabel(product, options);
  }

  /**
   * Descarga archivo .zpl
   */
  downloadZpl(product, options, filename = 'etiqueta_rp425.zpl') {
    const zpl = generateZplLabel(product, options);
    downloadZplFile(zpl, filename);
  }

  /**
   * Imprime ticket de venta con máxima nitidez y compatibilidad térmica
   */
  async printTicket(sale, settings = {}) {
    const paperWidth = String(settings.paperWidth || this.config?.paperWidth || '80');
    const is80mm = paperWidth === '80';
    const containerWidth = is80mm ? '80mm' : '58mm';
    const paperPageWidth = is80mm ? 80 : 58;

    // Datos del negocio: desde settings.biz, o desde localStorage, o desde la propia venta, o defaults
    let biz = settings.biz || {};
    if (!biz.business_name && !biz.name) {
      try {
        const storedBiz = localStorage.getItem('posent_business_data');
        if (storedBiz) biz = { ...JSON.parse(storedBiz), ...biz };
      } catch {}
    }

    const businessName = biz.business_name || biz.name || sale.business_name || settings.businessName || 'POSENT PRO';
    const rnc = biz.rnc || biz.tax_id || biz.cedula || sale.rnc || sale.tax_id || settings.rnc || '';
    const bizAddress = biz.address || biz.direccion || sale.business_address || '';
    const bizPhone = biz.phone || biz.telefono || sale.business_phone || '';
    const bizEmail = biz.email || sale.business_email || '';
    const bizLogoUrl = biz.logo || biz.logoUrl || biz.logo_url || '';
    const cajero = sale.vendedor_nombre || sale.sellerName || biz.cajero || settings.cajero || '';

    return new Promise(async (resolve, reject) => {
      try {
        let JsBarcodeLib = null;
        let QRCodeLib = null;
        try {
          JsBarcodeLib = (await import('jsbarcode')).default;
        } catch (e) {
          console.warn('JsBarcode import error in ticket:', e);
        }
        try {
          QRCodeLib = (await import('qrcode')).default;
        } catch (e) {
          console.warn('QRCode import error in ticket:', e);
        }

        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        iframe.style.zIndex = '-9999';
        document.body.appendChild(iframe);

        const docStream = iframe.contentWindow.document;
        const items = sale.items || [];
        const invoiceNum = sale.numero_venta || sale.invoice_number || sale.ticketNumber || (sale._id || sale.id ? `FAC-${String(sale._id || sale.id).slice(-6).toUpperCase()}` : 'TKT-01');
        const customerName = sale.cliente_nombre || sale.customerName || (sale.cliente_id?.nombre || sale.cliente_id?.name) || 'Cliente General';
        const saleDate = sale.fecha ? new Date(sale.fecha).toLocaleString() : (sale.date ? new Date(sale.date).toLocaleString() : new Date().toLocaleString());

        // Generar QR de la factura
        let qrDataUrl = null;
        if (QRCodeLib) {
          try {
            const qrValue = `FAC:${invoiceNum}|TOTAL:${sale.total || 0}|CLIENTE:${customerName}`;
            qrDataUrl = await QRCodeLib.toDataURL(qrValue, { width: 140, margin: 1, color: { dark: '#000000', light: '#ffffff' } });
          } catch (e) {
            console.warn('Error generando QR de ticket:', e);
          }
        }

        // Generar código de barras 1D de la factura para pistolas / lectores
        let barcodeDataUrl = null;
        if (JsBarcodeLib) {
          try {
            const canvas = document.createElement('canvas');
            JsBarcodeLib(canvas, invoiceNum, {
              format: 'CODE128',
              displayValue: true,
              fontSize: 13,
              font: 'Arial, sans-serif',
              textMargin: 2,
              margin: 4,
              height: 52,
              width: 1.8,
              background: '#ffffff',
              lineColor: '#000000'
            });
            barcodeDataUrl = canvas.toDataURL('image/png');
          } catch (e) {
            console.warn('Error generando código de barras de ticket:', e);
          }
        }
        let itemsHtml = items.map((item, idx) => {
          const name = item.name || item.nombre || 'Artículo';
          const qty = item.quantity || item.cantidad || 1;
          const unitPrice = Number(item.unit_price || item.precio_unitario || item.price || 0);
          const subtotal = Number(item.subtotal || (qty * unitPrice));

          return `
            <div style="margin-bottom: 2mm; padding-bottom: 1.5mm; border-bottom: ${idx < items.length - 1 ? '1px dotted #ccc' : 'none'};">
              <div style="font-weight: 900; font-size: 10.5pt; word-break: break-word; color: #000; line-height: 1.25;">
                ${name}
              </div>
              <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 2px; font-size: 10pt;">
                <span style="font-weight: 700; color: #000;">${qty} x $${unitPrice.toFixed(2)}</span>
                <span style="font-weight: 900; color: #000; font-size: 11pt;">$${subtotal.toFixed(2)}</span>
              </div>
            </div>
          `;
        }).join('');

        const subtotalVal = Number(sale.subtotal || sale.total || 0).toFixed(2);
        const taxVal = Number(sale.tax_amount || sale.impuestos || 0).toFixed(2);
        const totalVal = Number(sale.total || 0).toFixed(2);
        const paidVal = Number(sale.amount_paid || 0).toFixed(2);
        const balanceVal = Number(sale.balance_due ?? (sale.total - (sale.amount_paid || 0))).toFixed(2);

        docStream.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <meta charset="utf-8" />
              <title>Ticket ${invoiceNum}</title>
              <style>
                * {
                  box-sizing: border-box !important;
                  margin: 0;
                  padding: 0;
                }
                @page {
                  size: auto;
                  margin: 0mm !important;
                }
                html, body {
                  width: 100% !important;
                  max-width: 80mm !important;
                  height: auto !important;
                  min-height: 0 !important;
                  margin: 0 auto !important;
                  padding: 0 !important;
                  background: #ffffff !important;
                  color: #000000 !important;
                  font-family: Arial, "Helvetica Neue", Helvetica, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                  overflow: visible !important;
                }
                .ticket-box {
                  width: 100% !important;
                  max-width: 80mm !important;
                  margin: 0 auto !important;
                  padding: 2.5mm 3.5mm 10mm 3.5mm !important;
                  background: #ffffff !important;
                  color: #000000 !important;
                  page-break-inside: auto !important;
                  overflow: visible !important;
                }
                .text-center { text-align: center; }
                .text-right { text-align: right; }
                .bold { font-weight: 800; }
                .heavy { font-weight: 900; }
                .divider {
                  border-top: 2px dashed #000000;
                  margin: 2.5mm 0;
                }
              </style>
            </head>
            <body>
              <div class="ticket-box">
                <!-- ENCABEZADO COMERCIAL -->
                <div class="text-center" style="border-bottom: 2px dashed #000; padding-bottom: 2.5mm; margin-bottom: 2.5mm;">
                  ${bizLogoUrl ? `<div style="display: flex; justify-content: center; margin-bottom: 2mm;"><img src="${bizLogoUrl}" alt="logo" style="max-width: 55mm; max-height: 20mm; object-fit: contain; filter: contrast(200%) grayscale(100%);" /></div>` : ''}
                  <div class="heavy" style="font-size: 13.5pt; text-transform: uppercase; letter-spacing: -0.02em; line-height: 1.2;">${businessName}</div>
                  ${rnc ? `<div class="bold" style="font-size: 9.5pt; margin-top: 1mm;">RNC / CÉDULA: ${rnc}</div>` : ''}
                  ${bizAddress ? `<div class="bold" style="font-size: 9pt; margin-top: 0.8mm; word-break: break-word;">${bizAddress}</div>` : ''}
                  ${bizPhone ? `<div class="heavy" style="font-size: 9.5pt; margin-top: 0.8mm;">TEL: ${bizPhone}</div>` : ''}
                  ${bizEmail ? `<div class="bold" style="font-size: 8.5pt; margin-top: 0.5mm;">${bizEmail}</div>` : ''}
                </div>

                <!-- NÚMERO DE FACTURA Y FECHA -->
                <div class="text-center" style="border-bottom: 2px dashed #000; padding-bottom: 2.2mm; margin-bottom: 2.5mm;">
                  <div class="heavy" style="font-size: 12pt; text-transform: uppercase; letter-spacing: 0.04em;">FACTURA DE VENTA</div>
                  <div class="heavy" style="font-size: 11.5pt; letter-spacing: 0.02em; margin-top: 0.5mm;">NO: ${invoiceNum}</div>
                  <div class="bold" style="font-size: 9pt; margin-top: 0.8mm;">FECHA: ${saleDate}</div>
                </div>

                <!-- CLIENTE Y CAJERO -->
                <div style="border-bottom: 2px dashed #000; padding-bottom: 2.2mm; margin-bottom: 2.5mm; font-size: 9.5pt; display: flex; flex-direction: column; gap: 0.8mm;">
                  <div style="display: flex; justify-content: space-between;">
                    <span class="bold">CLIENTE:</span>
                    <span class="heavy" style="text-align: right; max-width: 70%; word-break: break-word;">${customerName}</span>
                  </div>
                  ${cajero ? `<div style="display: flex; justify-content: space-between;"><span class="bold">CAJERO:</span><span class="heavy">${cajero}</span></div>` : ''}
                </div>

                <!-- ARTÍCULOS -->
                <div style="border-bottom: 2px dashed #000; padding-bottom: 2.2mm; margin-bottom: 2.5mm;">
                  <div style="display: flex; justify-content: space-between; font-weight: 900; font-size: 10pt; border-bottom: 1.5px solid #000; padding-bottom: 1.2mm; margin-bottom: 2mm;">
                    <span>DESCRIPCIÓN DEL ARTÍCULO</span>
                    <span>TOTAL</span>
                  </div>
                  <div>${itemsHtml}</div>
                </div>

                <!-- TOTALES -->
                <div style="border-bottom: 2px dashed #000; padding-bottom: 2.5mm; margin-bottom: 2.5mm; font-size: 10pt; display: flex; flex-direction: column; gap: 0.8mm;">
                  <div style="display: flex; justify-content: space-between; font-weight: 800;">
                    <span>SUBTOTAL:</span><span class="heavy">$${subtotalVal}</span>
                  </div>
                  ${Number(taxVal) > 0 ? `
                    <div style="display: flex; justify-content: space-between; font-weight: 800;">
                      <span>ITBIS (18%):</span><span class="heavy">$${taxVal}</span>
                    </div>
                  ` : ''}
                  <div style="display: flex; justify-content: space-between; font-size: 13.5pt; font-weight: 900; border-top: 2px solid #000; border-bottom: 2px solid #000; padding: 2mm 0; margin: 1.5mm 0; letter-spacing: -0.02em;">
                    <span>TOTAL:</span>
                    <span>$${totalVal}</span>
                  </div>
                  ${Number(paidVal) > 0 ? `
                    <div style="display: flex; justify-content: space-between; font-weight: 800; font-size: 10.5pt; margin-top: 0.8mm;">
                      <span>PAGADO:</span><span class="heavy">$${paidVal}</span>
                    </div>
                    ${Number(balanceVal) > 0 ? `
                      <div style="display: flex; justify-content: space-between; font-weight: 900; font-size: 10.5pt; margin-top: 0.8mm;">
                        <span>RESTANTE PENDIENTE:</span><span>$${balanceVal}</span>
                      </div>
                    ` : `<div class="text-center heavy" style="font-size: 9.5pt; margin-top: 1.5mm;">*** FACTURA SALDADA / PAGADA ***</div>`}
                  ` : ''}
                </div>

                <!-- CÓDIGO DE BARRAS + QR -->
                <div style="display: flex; flex-direction: column; align-items: center; width: 100%; overflow: hidden;">
                  ${barcodeDataUrl ? `
                    <img src="${barcodeDataUrl}" alt="${invoiceNum}" style="max-width: 98%; height: 16mm; object-fit: contain; image-rendering: crisp-edges; margin-bottom: 1.5mm;" />
                  ` : ''}
                  ${qrDataUrl ? `
                    <div style="display: flex; align-items: center; justify-content: center; gap: 4mm; margin-top: 1.5mm;">
                      <img src="${qrDataUrl}" alt="QR" style="width: 22mm; height: 22mm; object-fit: contain; image-rendering: pixelated;" />
                      <div style="text-align: left; font-size: 8.5pt; font-weight: 800; line-height: 1.3;">
                        <div>COMPROBANTE VÁLIDO</div>
                        <div style="font-size: 7.5pt; font-weight: 600;">Escanee con lector o cámara</div>
                      </div>
                    </div>
                  ` : ''}
                </div>

                <!-- PIE DE PÁGINA -->
                <div class="divider"></div>
                <div class="text-center" style="font-size: 8.5pt; font-weight: 800; margin-top: 2mm;">
                  ¡GRACIAS POR SU COMPRA!<br />
                  <span style="font-size: 7.5pt; font-weight: 600;">POSENT PRO · TECNOLOGÍA COMERCIAL</span>
                </div>
              </div>
              <script>
                window.onload = function() {
                  setTimeout(function() {
                    window.focus();
                    window.print();
                    setTimeout(function() {
                      if (window.parent && window.frameElement) {
                        window.parent.document.body.removeChild(window.frameElement);
                      }
                    }, 400);
                  }, 250);
                };
              </script>
            </body>
          </html>
        `);
        docStream.close();
        resolve(true);
      } catch (err) {
        console.error('Error al imprimir ticket:', err);
        reject(err);
      }
    });
  }

  /**
   * Imprime página de prueba de la impresora
   */
  async testPrint(type = 'label') {
    if (type === 'label') {
      const testProduct = {
        name: 'PRUEBA RONGTA RP425',
        code: 'RP425-TEST-2026',
        price: 99.99,
        category: 'CALIBRACION',
      };
      return this.printLabel(testProduct, {
        size: this.config?.defaultLabelSize || '50x25',
        copies: 1,
        showPrice: true,
        showName: true,
        showCode: true,
        showCategory: true,
        showBusinessName: true,
        businessName: 'RONGTA RP425 POSENT',
      });
    } else {
      const testSale = {
        invoice_number: 'TEST-RP425',
        business_name: 'POSENT - PRUEBA RP425',
        items: [
          { name: 'Articulo de Prueba 1', quantity: 2, unit_price: 150.0, subtotal: 300.0 },
          { name: 'Servicio Tecnico Especial', quantity: 1, unit_price: 500.0, subtotal: 500.0 }
        ],
        subtotal: 800.0,
        tax_amount: 144.0,
        total: 944.0,
        amount_paid: 1000.0,
        balance_due: 0.0
      };
      return this.printTicket(testSale, { paperWidth: '80' });
    }
  }

  // Métodos de compatibilidad
  async printViaUSB(content, config) {
    return this.testPrint('label');
  }

  async printViaNetwork(content, config) {
    return this.testPrint('ticket');
  }

  async printViaBluetooth(content, config) {
    return this.testPrint('label');
  }

  getStatus() {
    return {
      connected: true,
      model: this.config?.model || 'RONGTA RP425 (ZPL 203DPI)',
      type: this.config?.printerType || 'thermal_label',
      config: this.config
    };
  }
}

export const printerService = new PrinterService();
export default printerService;